import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Typography,
} from '@mui/material';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { MusicFolderNode } from '../../models/Zone';

interface FolderTreeProps {
  nodes: MusicFolderNode[];
  selectedPaths: string[];
  onSelectionChange: (paths: string[]) => void;
}

export const FolderTree = ({ nodes, selectedPaths, onSelectionChange }: FolderTreeProps) => {
  // Add UI state to the nodes
  const [treeNodes, setTreeNodes] = useState<MusicFolderNode[]>([]);

  // Initialize the tree with expanded/selected states
  useEffect(() => {
    const initializeNodes = (nodes: MusicFolderNode[]): MusicFolderNode[] => {
      return nodes.map(node => {
        // Check if this node or any descendants are selected
        const isNodeSelected = selectedPaths.includes(node.path);
        const hasSelectedChildren = node.children.some(child =>
          selectedPaths.includes(child.path) ||
          selectedPaths.some(p => p.startsWith(`${child.path}/`))
        );

        return {
          ...node,
          isSelected: isNodeSelected,
          isExpanded: hasSelectedChildren || isNodeSelected,
          children: initializeNodes(node.children)
        };
      });
    };

    setTreeNodes(initializeNodes(nodes));
  }, [nodes, selectedPaths]);

  // Handle toggling folder expansion
  const toggleExpand = (path: string) => {
    setTreeNodes(prevNodes => {
      const updateNodes = (nodes: MusicFolderNode[]): MusicFolderNode[] => {
        return nodes.map(node => {
          if (node.path === path) {
            return { ...node, isExpanded: !node.isExpanded };
          } else if (node.children.length > 0) {
            return {
              ...node,
              children: updateNodes(node.children)
            };
          }
          return node;
        });
      };
      return updateNodes(prevNodes);
    });
  };

  // Handle selection changes
  const handleNodeSelectionChange = (node: MusicFolderNode, checked: boolean) => {
    // Get all paths that should be selected/deselected (this node and all descendants)
    const getAffectedPaths = (node: MusicFolderNode): string[] => {
      let paths = [node.path];
      node.children.forEach(child => {
        paths = [...paths, ...getAffectedPaths(child)];
      });
      return paths;
    };

    const affectedPaths = getAffectedPaths(node);

    // Update selected paths
    let newSelectedPaths: string[];
    if (checked) {
      // Add all affected paths that aren't already selected (avoiding Set for compatibility)
      const combinedPaths = [...selectedPaths];
      affectedPaths.forEach(path => {
        if (!combinedPaths.includes(path)) {
          combinedPaths.push(path);
        }
      });
      newSelectedPaths = combinedPaths;
    } else {
      // Remove all affected paths
      newSelectedPaths = selectedPaths.filter(p => !affectedPaths.includes(p));
    }

    // Notify parent about the change
    onSelectionChange(newSelectedPaths);
  };

  // Recursive component to render tree nodes
  const renderTree = (nodes: MusicFolderNode[], depth: number = 0) => {
    return nodes.map((node) => {
      const isSelected = selectedPaths.includes(node.path);
      const hasChildren = node.children && node.children.length > 0;

      return (
        <Box key={node.path}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: depth * 2.5 + 0.5,
              pr: 1,
              py: 0.5,
              borderRadius: '8px',
              mx: 0.5,
              my: 0.25,
              cursor: 'pointer',
              transition: 'all 0.15s',
              backgroundColor: isSelected ? 'rgba(66, 148, 227, 0.06)' : 'transparent',
              '&:hover': {
                backgroundColor: isSelected ? 'rgba(66, 148, 227, 0.1)' : 'rgba(0,0,0,0.03)',
              },
            }}
            onClick={() => handleNodeSelectionChange(node, !isSelected)}
          >
            {/* Expand/Collapse arrow */}
            {hasChildren ? (
              <Box
                onClick={(e) => { e.stopPropagation(); toggleExpand(node.path); }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: '6px', flexShrink: 0,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                }}
              >
                {node.isExpanded
                  ? <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: '#888' }} />
                  : <ChevronRightRoundedIcon sx={{ fontSize: 16, color: '#bbb' }} />
                }
              </Box>
            ) : (
              <Box sx={{ width: 22, flexShrink: 0 }} />
            )}

            {/* Checkbox */}
            <Checkbox
              checked={isSelected}
              size="small"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleNodeSelectionChange(node, e.target.checked)}
              sx={{
                p: 0, color: '#ccc',
                '&.Mui-checked': { color: '#4294e3' }
              }}
            />

            {/* Folder icon */}
            {isSelected
              ? <FolderOpenRoundedIcon sx={{ fontSize: 18, color: '#4294e3', flexShrink: 0 }} />
              : <FolderRoundedIcon sx={{ fontSize: 18, color: '#bbb', flexShrink: 0 }} />
            }

            {/* Folder name */}
            <Typography sx={{
              fontSize: '0.85rem',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? '#4294e3' : '#1a1a2e',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              flex: 1, ml: 0.5
            }}>
              {node.name}
            </Typography>
          </Box>

          {/* Render children if expanded */}
          {hasChildren && (
            <Collapse in={node.isExpanded} timeout="auto" unmountOnExit>
              {renderTree(node.children, depth + 1)}
            </Collapse>
          )}
        </Box>
      );
    });
  };

  return (
    <Box sx={{
      maxHeight: '300px',
      overflow: 'auto',
      py: 0.5,
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.12)',
        borderRadius: '3px',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' }
      },
      '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(0,0,0,0.12) transparent',
    }}>
      {treeNodes.length > 0 ? (
        renderTree(treeNodes)
      ) : (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>
            Müzik klasörleri yükleniyor...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FolderTree;
